import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScanLine, MousePointerClick, TrendingUp, Trophy } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useChartTheme } from "@/contexts/ThemeContext";

export default function AdminAnalytics() {
  const chartTheme = useChartTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/analytics").then((res) => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="animate-fade-in">
        <h1 className="font-heading text-2xl font-bold mb-2">Platform Analytics</h1>
        <Card><CardContent className="py-12 text-center text-muted-foreground">No data available</CardContent></Card>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-bold mb-1">Platform Analytics</h1>
        <p className="text-muted-foreground text-sm">Global activity across all users</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="admin-analytics-stats">
        <Card className="card-hover">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-2.5 rounded-lg bg-cyan-500/10 text-cyan-400">
              <ScanLine size={20} />
            </div>
            <div>
              <p className="text-3xl font-heading font-bold">{data.total_scans}</p>
              <p className="text-xs text-muted-foreground">Total Scans</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="flex items-center gap-4 p-5">
            <div className="p-2.5 rounded-lg bg-pink-500/10 text-pink-400">
              <MousePointerClick size={20} />
            </div>
            <div>
              <p className="text-3xl font-heading font-bold">{data.total_clicks}</p>
              <p className="text-xs text-muted-foreground">Total Link Clicks</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {data.daily_scans?.length > 0 && (
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <TrendingUp size={16} className="text-amber-400" /> Daily Platform Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64" data-testid="admin-daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.daily_scans}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridColor} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: chartTheme.tickColor }} />
                  <YAxis tick={{ fontSize: 11, fill: chartTheme.tickColor }} />
                  <Tooltip contentStyle={chartTheme.tooltipStyle} />
                  <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <Trophy size={16} className="text-amber-400" /> Top Profiles
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_profiles?.length > 0 ? (
              <Table data-testid="admin-top-profiles">
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Profile</TableHead>
                    <TableHead>Token</TableHead>
                    <TableHead className="text-right">Scans</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_profiles.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm font-medium">{i + 1}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">@{p.username}</p>
                          {p.title && <p className="text-xs text-muted-foreground">{p.title}</p>}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="secondary" className="font-mono text-xs">{p.token}</Badge></TableCell>
                      <TableCell className="text-right font-heading font-bold">{p.scan_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No scan data yet</p>
            )}
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <MousePointerClick size={16} className="text-amber-400" /> Top Links
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_links?.length > 0 ? (
              <Table data-testid="admin-top-links">
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_links.map((l, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm font-medium">{i + 1}</TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{l.label}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">{l.url}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-heading font-bold">{l.click_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No click data yet</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
